import {
  logs,
  SeverityNumber,
  type AnyValue,
  type AnyValueMap,
} from '@opentelemetry/api-logs';
import { INSTRUMENTATION } from 'constant/instrumentation';
import {
  getNormalizedPathname,
  getWindowLocationPathname,
} from 'utils/functions/window';
import { LOCAL_STORAGE_KEYS } from 'constant/localStorage';

const logger = logs.getLogger(INSTRUMENTATION.SERVICE_NAME);

/** Read live user/tenant identity at emit time so logs are accurate even when
 *  the OTel resource was frozen before login. These attributes override the
 *  stale resource-level values in SigNoz's indexing. */
function getLiveUserAttrs(): AnyValueMap {
  return {
    screenName: getNormalizedPathname(), // /projects/:id — normalised, for grouping
    screenPath: getWindowLocationPathname(), // /projects/123 — record ids are opaque UUIDs (non-PII); pathname carries no query string
    // Identifiers only — never PII content. user.email / user.name / tenant.name
    // are deliberately omitted; user.id / tenant.id (UUIDs) join back to identity
    // in the DB for whoever needs it. Collector redaction is the backstop.
    'user.id':
      localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID) ?? 'UnknownUser',
    'tenant.id':
      localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT) ?? 'UnknownTenant',
  };
}

/**
 * @param body - Use body for the main message
 * @param attrs - attributes for structured data
 */
export function emitInfoLog({
  body,
  attrs,
  severityNumber = SeverityNumber.INFO,
}: {
  body: AnyValue;
  attrs?: AnyValueMap;
  severityNumber?: SeverityNumber;
}) {
  logger.emit({
    body: body,
    severityNumber,
    severityText: 'INFO',
    attributes: {
      ...attrs,
      ...getLiveUserAttrs(),
    },
  });
}

export function emitWarnLog({
  body,
  attrs,
  severityNumber = SeverityNumber.WARN,
}: {
  body: AnyValue;
  attrs?: AnyValueMap;
  severityNumber?: SeverityNumber;
}) {
  logger.emit({
    body,
    severityNumber,
    severityText: 'WARN',
    attributes: {
      ...attrs,
      ...getLiveUserAttrs(),
    },
  });
}

export function emitErrorLog({
  body,
  attrs,
  severityNumber = SeverityNumber.ERROR,
}: {
  body: AnyValue;
  attrs?: AnyValueMap;
  severityNumber?: SeverityNumber;
}) {
  logger.emit({
    body,
    severityNumber,
    severityText: 'ERROR',
    attributes: {
      ...attrs,
      ...getLiveUserAttrs(),
    },
  });
}

export function emitFatalLog({
  body,
  attrs,
  severityNumber = SeverityNumber.FATAL,
}: {
  body: AnyValue;
  attrs?: AnyValueMap;
  severityNumber?: SeverityNumber;
}) {
  logger.emit({
    body,
    severityNumber,
    severityText: 'FATAL',
    attributes: {
      ...attrs,
      ...getLiveUserAttrs(),
    },
  });
}
